import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, Grid, Typography } from '@material-ui/core';
import PaymentIcon from '@material-ui/icons/Payment';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import ContentLayout from 'views/layout/ContentLayout';
import LayoutTabContent from 'views/layout/LayoutTabContent';
import {
    useCreateMerchantSubscriptionCheckoutMutation,
    useCreateMerchantSubscriptionPortalMutation,
    useMerchantSubscriptionChargesQuery,
    useMerchantSubscriptionPricesQuery,
    useMerchantSubscriptionStatusQuery
} from 'services/payment';
import { useLocation } from 'react-router-dom';
import ChargesTable from './ChargesTable';

const VALID_SUBSCRIPTION_STATUSES = ['active', 'trialing'];

const useStyles = makeStyles((theme) => ({
    content: {
        height: '100%',
        overflowY: 'auto',
        padding: theme.spacing(3)
    },
    panel: {
        maxWidth: 920,
        margin: '0 auto',
        width: '100%'
    },
    status: {
        padding: theme.spacing(2),
        borderRadius: 8,
        border: `1px solid ${theme.colors.border}`,
        background: theme.palette.common.white,
        marginBottom: theme.spacing(3)
    },
    priceOption: {
        position: 'relative',
        padding: theme.spacing(3),
        borderRadius: 8,
        border: `1px solid ${theme.colors.border}`,
        background: theme.palette.common.white,
        minHeight: 170
    },
    defaultPriceOption: {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 2px ${theme.palette.primary.main}22`
    },
    defaultTag: {
        position: 'absolute',
        top: 12,
        right: 12,
        padding: '2px 8px',
        borderRadius: 4,
        background: theme.colors.anakiwa,
        color: theme.colors.primaryText,
        fontSize: 12,
        fontWeight: 700
    },
    interval: {
        textTransform: 'capitalize',
        marginBottom: theme.spacing(1)
    },
    amount: {
        fontSize: 32,
        lineHeight: '40px',
        fontWeight: 700,
        color: theme.colors.primaryText
    },
    subText: {
        color: theme.colors.steel,
        marginTop: theme.spacing(1)
    },
    actions: {
        marginTop: theme.spacing(3)
    },
    btnSubmit: {
        position: 'relative'
    },
    buttonProgress: {
        color: theme.palette.common.white,
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -10,
        marginLeft: -10
    },
    message: {
        color: theme.colors.steel
    }
}));

function formatAmount(amount, currency) {
    const value = Number(amount || 0) / 100;
    try {
        return new Intl.NumberFormat('en-CA', {
            style: 'currency',
            currency: (currency || 'cad').toUpperCase()
        }).format(value);
    } catch (error) {
        return `${(currency || 'cad').toUpperCase()} ${value.toFixed(2)}`;
    }
}

function formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString();
}

function getRouteMessage(pathname) {
    if (pathname === '/subscription/success') {
        return 'Subscription checkout completed. Refreshing billing status from BNS.';
    }
    if (pathname === '/subscription/cancel') {
        return 'Subscription checkout was canceled. You can add a card when you are ready.';
    }
    return '';
}

function getDisplayInterval({ status, prices }) {
    return status?.subscription?.interval || prices?.defaultInterval || '-';
}

function getSubscriptionEndDate(subscription) {
    if (!subscription) return '-';
    const hasKnownEnd = subscription.cancelAtPeriodEnd || ['canceled', 'unpaid', 'paused'].includes(subscription.status);
    if (!hasKnownEnd) return '-';
    return formatDate(subscription.currentPeriodEnd) || '-';
}

function getStatusLabel(subscription) {
    if (!subscription?.status) return 'No subscription';
    return subscription.status;
}

function Subscription() {
    const classes = useStyles();
    const location = useLocation();
    const [error, setError] = React.useState('');

    const routeMessage = React.useMemo(() => getRouteMessage(location.pathname), [location.pathname]);

    const statusQuery = useMerchantSubscriptionStatusQuery({
        onError: (statusError) => {
            setError(statusError?.message || 'Load subscription status failed');
        }
    });

    const pricesQuery = useMerchantSubscriptionPricesQuery({
        onError: (priceError) => {
            if (statusQuery.data?.requireSubscription) {
                setError(priceError?.message || 'Merchant subscription price is not configured');
            }
        }
    });
    const chargesQuery = useMerchantSubscriptionChargesQuery({
        enabled: !!statusQuery.data?.subscription?.stripeCustomerId
    });

    const checkoutMutation = useCreateMerchantSubscriptionCheckoutMutation({
        onMutate: () => {
            setError('');
        },
        onSuccess: (checkout) => {
            window.location.href = checkout.url;
        },
        onError: (checkoutError) => {
            setError(checkoutError?.message || 'Create subscription checkout failed');
        }
    });

    const portalMutation = useCreateMerchantSubscriptionPortalMutation({
        onMutate: () => {
            setError('');
        },
        onSuccess: (portal) => {
            window.location.href = portal.url;
        },
        onError: (portalError) => {
            setError(portalError?.message || 'Open billing portal failed');
        }
    });

    React.useEffect(() => {
        if (location.pathname === '/subscription/success') {
            statusQuery.refetch();
            pricesQuery.refetch();
            chargesQuery.refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    React.useEffect(() => {
        if (statusQuery.error || pricesQuery.error) return;
        setError('');
    }, [statusQuery.error, pricesQuery.error]);

    const status = statusQuery.data;
    const prices = pricesQuery.data;
    const isFetching = statusQuery.isLoading || pricesQuery.isLoading;
    const displayError = error && (!prices || checkoutMutation.isError || portalMutation.isError || statusQuery.isError);
    const displayInterval = getDisplayInterval({ status, prices });
    const subscriptionEndDate = getSubscriptionEndDate(status?.subscription);
    const subscriptionStatus = getStatusLabel(status?.subscription);
    const isCancelAtPeriodEnd = !!status?.subscription?.cancelAtPeriodEnd;

    const isSubscriptionOk = React.useMemo(() => {
        return !status?.requireSubscription || VALID_SUBSCRIPTION_STATUSES.includes(status?.subscription?.status);
    }, [status]);

    const handleCheckout = React.useCallback(async () => {
        checkoutMutation.mutate({});
    }, [checkoutMutation]);

    const handlePortal = React.useCallback(async () => {
        portalMutation.mutate();
    }, [portalMutation]);

    const renderPriceOption = React.useCallback(
        (interval) => {
            const price = prices?.[interval];
            if (!price) return null;
            const isDefault = prices?.defaultInterval === interval;

            return (
                <Grid item xs={12} sm={6}>
                    <div className={`${classes.priceOption} ${isDefault ? classes.defaultPriceOption : ''}`}>
                        {isDefault && <div className={classes.defaultTag}>Default</div>}
                        <Typography className={classes.interval} variant="body1">
                            {interval}
                        </Typography>
                        <Typography className={classes.amount}>{formatAmount(price.amount, prices?.currency)}</Typography>
                        <Typography className={classes.subText} variant="body2">
                            Charged {interval === 'yearly' ? 'once per year' : 'once per month'} after any configured
                            trial period.
                        </Typography>
                    </div>
                </Grid>
            );
        },
        [classes, prices]
    );

    return (
        <ContentLayout title="Subscription" titleIcon={<PaymentIcon />}>
            <LayoutTabContent isLoading={isFetching}>
                <div className={classes.content}>
                    <Grid className={classes.panel} container direction="column">
                        <Grid item className={classes.status}>
                            <Grid container direction="column" spacing={1}>
                                {routeMessage && (
                                    <Grid item>
                                        <Typography className={classes.message} variant="body2">
                                            {routeMessage}
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item>
                                    <Typography variant="body1">Status: {subscriptionStatus}</Typography>
                                </Grid>
                                {isCancelAtPeriodEnd && (
                                    <Grid item>
                                        <Typography color="secondary" variant="body2">
                                            Subscription has been canceled and will end on {subscriptionEndDate}.
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item>
                                    <Typography className={classes.message} variant="body2">
                                        Interval: {displayInterval}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography className={classes.message} variant="body2">
                                        End date: {subscriptionEndDate}
                                    </Typography>
                                </Grid>
                                {displayError && (
                                    <Grid item>
                                        <Typography color="secondary" variant="body2">
                                            {error}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>

                        {prices && !isSubscriptionOk && (
                            <Grid item>
                                <Grid container spacing={2}>
                                    {renderPriceOption('monthly')}
                                    {renderPriceOption('yearly')}
                                </Grid>
                            </Grid>
                        )}

                        <Grid item className={classes.actions}>
                            <Grid container spacing={2} justifyContent="flex-end">
                                {status?.subscription?.stripeCustomerId && (
                                    <Grid item className={classes.btnSubmit}>
                                        <Button
                                            disabled={portalMutation.isLoading}
                                            onClick={handlePortal}
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<CreditCardIcon />}
                                        >
                                            {portalMutation.isLoading ? (
                                                <CircularProgress size={20} className={classes.buttonProgress} />
                                            ) : (
                                                'Manage Card'
                                            )}
                                        </Button>
                                    </Grid>
                                )}
                                {!isSubscriptionOk && prices && (
                                    <Grid item className={classes.btnSubmit}>
                                        <Button
                                            disabled={checkoutMutation.isLoading}
                                            onClick={handleCheckout}
                                            variant="contained"
                                            color="primary"
                                            startIcon={<CreditCardIcon />}
                                        >
                                            {checkoutMutation.isLoading ? (
                                                <CircularProgress size={20} className={classes.buttonProgress} />
                                            ) : (
                                                'Add Card'
                                            )}
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        {status?.subscription?.stripeCustomerId && (
                            <Grid item>
                                <ChargesTable
                                    charges={chargesQuery.data || []}
                                    isLoading={chargesQuery.isLoading}
                                    error={chargesQuery.error}
                                />
                            </Grid>
                        )}
                    </Grid>
                </div>
            </LayoutTabContent>
        </ContentLayout>
    );
}

export default React.memo(Subscription);
